import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AgGridTable, Pagination, DateRangePicker } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { MdAssignment, MdFilterAlt } from 'react-icons/md';
import { FaSearch, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { getEstimateAssigmentSchedule, exportEstimateAssignmentSchedule } from 'redux/crm/report/reportSlice';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import AdvanceFilterDrawer, { getEmptyValues } from 'components/common/AdvanceFilterDrawer';
import SalesUnitGroupedDropdown from 'components/app/crm/shared/SalesUnitGroupedDropdown';
import { Link } from 'react-router-dom';
import { debounce } from 'helpers/debounce';
import NumberRenderer from 'components/common/ag-grid/NumberRenderer';
import JobFormatRenderer from 'components/app/crm/reports/components/JobFormatRenderer';
import DateFormatRenderer from 'components/app/crm/reports/components/DateFormatRenderer';

const SEARCH_FIELD = 'project_q_number_or_project_j_number_or_project_name_cont';

const THIS_MONTH_START = dayjs().startOf('month').format('YYYY-MM-DD');
const THIS_MONTH_END   = dayjs().format('YYYY-MM-DD');

const FILTER_FIELDS = [
  { key: 'project_name',   label: 'Project Name',    type: 'text', paramKey: 'project_name_cont',           tagName: 'Project' },
  { key: 'sales_incharge', label: 'Sales In-charge',  type: 'text', paramKey: 'project_sales_incharge_cont', tagName: 'Sales'   },
  { key: 'created_at',     label: 'Created At',       type: 'date_range_picker', paramKeys: ['created_at_gteq', 'created_at_lteq'], tagName: 'Created' },
];

const COLUMN_DEFS = [
 { 
     headerName: 'ID#',
     field: 'id',
     hide: true,
   },
   { 
     headerName: 'Quote number.',
     field: 'project.q_number',
     minWidth: 140,
     cellRenderer: (params) => {
       const {project} = params.data
       return (
         <Link to={`/crm/projects/${project.id}`}>{project.q_number}</Link>
       )
     },
   },
   { 
     headerName: 'P/J number',
     field: 'building.j_number',
     minWidth: 140,
     cellRenderer: JobFormatRenderer,
   },
   { 
     headerName: 'Project name',
     field: 'project.name',
     minWidth: 200,
     cellRenderer: (params) => {
       const {project} = params.data
       return (
         <Link to={`/crm/projects/${project.id}`}>{project.name}</Link>
       )
     }
   },
   { 
     headerName: 'Building',
     field: 'building.name',
     minWidth: 100,
   },
   { 
     headerName: 'Description',
     field: 'building.description',
     minWidth: 180,
   },
   { 
     headerName: 'Location',
     field: 'project.pj_location',
     minWidth: 200,
   },
   { 
     headerName: 'REV',
     field: 'rev',
     minWidth: 100,
   },
   { 
     headerName: 'Sales incharge',
     field: 'project.sales_incharge',
     minWidth: 120,
     cellRenderer: (params) => {
       return <span className="tw-text-center tw-text-blue-600">{params.value}</span>
     }
   },
   { 
     headerName: 'Request by',
     field: 'est_requested_by',
     minWidth: 130,
     cellRenderer: (params) => {
       return <span className="tw-text-center tw-text-blue-600">{params.value}</span>
     }
   },
   { 
     headerName: 'Request date',
     field: 'est_requested_date',
     minWidth: 150,
     cellRenderer: DateFormatRenderer
   },
   { 
     headerName: 'Schedule date',
     field: 'est_scheduled_date',
     minWidth: 150,
     cellRenderer: DateFormatRenderer
   },
   { 
     headerName: 'Release date',
     field: 'est_released_date',
     minWidth: 150,
     cellRenderer: DateFormatRenderer
   },
   { 
     headerName: 'Weight (M.T)',
     field: 'est_released_weight',
     minWidth: 110,
     cellRenderer: NumberRenderer,
   },
   { 
     headerName: 'Designer',
     field: 'dsn_incharge',
     minWidth: 110,
     cellRenderer: (params) => {
       return <span className="tw-text-center tw-text-blue-600">{params.value}</span>
     }
   },
   { 
     headerName: 'Checker',
     field: 'des_incharge',
     minWidth: 110,
     cellRenderer: (params) => {
       return <span className="tw-text-center tw-text-blue-600">{params.value}</span>
     }
   },
   { 
     headerName: "Note",
     field: "est_eng_note",
     minWidth: 160,
     cellRenderer: (params) => {
       return <span className="tw-text-red-600">{params.value}</span>
     }
   },
   { 
     headerName: 'Sales unit',
     field: 'sales_unit.description',
     minWidth: 130,
   },
   { 
     headerName: 'Region',
     field: 'sales_unit.region',
     minWidth: 130,
   },  
   { 
     headerName: 'Created by',
     field: 'created_by',
     minWidth: 120,
     cellRenderer: (params) => {
       return <span className="tw-text-center">{params.value}</span>
     }
   },
   { 
     headerName: 'Created date',
     field: 'created_date',
     minWidth: 150
   }
];

const EstimateAssignmentSchedule = () => {
  const dispatch = useDispatch();

  const rowData              = useSelector((s) => s.crm_report.estimateAssigmentScheduleData.data);
  const totalRows            = useSelector((s) => s.crm_report.estimateAssigmentScheduleData.totalRows);
  const exporting            = useSelector((s) => s.crm_report.estimateAssigmentScheduleData.exporting);
  const { activeSalesUnitsData = [] } = useSelector((s) => s.crm_sales_unit);

  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sort, setSort]       = useState({ attribute: 'id', direction: 'desc' });
  const [search, setSearch]   = useState('');
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(FILTER_FIELDS));
  const [filterTags, setFilterTags]     = useState([]);
  const [showFilter, setShowFilter]     = useState(false);
  const [loading, setLoading]           = useState(false);

  // Quick filters
  const [quickReqDate,      setQuickReqDate]      = useState({ startDate: THIS_MONTH_START, endDate: THIS_MONTH_END });
  const [quickSchDate,      setQuickSchDate]      = useState({ startDate: null, endDate: null });
  const [quickRelDate,      setQuickRelDate]      = useState({ startDate: null, endDate: null });
  const [quickSalesUnitIds, setQuickSalesUnitIds] = useState([]);

  const quickReqDateRef      = useRef({ startDate: THIS_MONTH_START, endDate: THIS_MONTH_END });
  const quickSchDateRef      = useRef({ startDate: null, endDate: null });
  const quickRelDateRef      = useRef({ startDate: null, endDate: null });
  const quickSalesUnitIdsRef = useRef([]);

  const activeFilterParamsRef = useRef({});
  const searchRef = useRef('');

  const totalPages = Math.ceil(totalRows / perPage) || 1;

  useEffect(() => { dispatch(getActiveSalesUnits()); }, [dispatch]);

  // Reads refs only, so it is stable for the component's lifetime.
  const buildQuickParams = useCallback(() => ({
    ...(quickReqDateRef.current.startDate
      ? { est_requested_date_gteq: quickReqDateRef.current.startDate, est_requested_date_lteq: quickReqDateRef.current.endDate }
      : {}),
    ...(quickSchDateRef.current.startDate
      ? { est_scheduled_date_gteq: quickSchDateRef.current.startDate, est_scheduled_date_lteq: quickSchDateRef.current.endDate }
      : {}),
    ...(quickRelDateRef.current.startDate
      ? { est_released_date_gteq: quickRelDateRef.current.startDate, est_released_date_lteq: quickRelDateRef.current.endDate }
      : {}),
    ...(quickSalesUnitIdsRef.current.length > 0
      ? { sales_unit_id_in: quickSalesUnitIdsRef.current }
      : {}),
  }), []);

  const buildParams = useCallback(
    (overrides = {}) => ({
      page, per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }),
      ...buildQuickParams(),
      ...activeFilterParamsRef.current,
      ...overrides,
    }),
    [page, perPage, sort, buildQuickParams]
  );

  const fetchData = useCallback(async (params) => {
    setLoading(true);
    try { await dispatch(getEstimateAssigmentSchedule(params)); } finally { setLoading(false); }
  }, [dispatch]);

  useEffect(() => { fetchData(buildParams()); }, [fetchData, buildParams]);

  const debouncedFetch = useRef(debounce((params) => fetchData(params), 500)).current;

  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    setPage(1);
    debouncedFetch({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(val && { [SEARCH_FIELD]: val }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleClearSearch = () => {
    searchRef.current = '';
    setSearch('');
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleReqDateChange = ({ startDate, endDate }) => {
    quickReqDateRef.current = { startDate, endDate };
    setQuickReqDate({ startDate, endDate });
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleSchDateChange = ({ startDate, endDate }) => {
    quickSchDateRef.current = { startDate, endDate };
    setQuickSchDate({ startDate, endDate });
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleRelDateChange = ({ startDate, endDate }) => {
    quickRelDateRef.current = { startDate, endDate };
    setQuickRelDate({ startDate, endDate });
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleSalesUnitChange = (ids) => {
    quickSalesUnitIdsRef.current = ids;
    setQuickSalesUnitIds(ids);
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleSort = useCallback(({ colId, sort: dir }) => {
    const newSort = dir ? { attribute: colId, direction: dir } : { attribute: 'id', direction: 'desc' };
    setSort(newSort);
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort: newSort, ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickParams(), ...activeFilterParamsRef.current });
  }, [fetchData, perPage, buildQuickParams]);

  const handleApplyFilter = (params, tags) => {
    activeFilterParamsRef.current = params;
    setFilterTags(tags);
    setPage(1);
    setShowFilter(false);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickParams(), ...params });
  };

  const handleResetFilter = () => {
    activeFilterParamsRef.current = {};
    setFilterValues(getEmptyValues(FILTER_FIELDS));
    setFilterTags([]);
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickParams() });
  };

  const removeTag = (tag) => {
    const newParams = Object.fromEntries(Object.entries(activeFilterParamsRef.current).filter(([k]) => k !== tag.id));
    activeFilterParamsRef.current = newParams;
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickParams(), ...newParams });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
            <div className="tw-flex tw-items-center tw-gap-2">
              <MdAssignment className="tw-text-amber-600" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Estimate Assignment Schedule</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Track estimation task assignments and scheduling.</p>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <button type="button" onClick={() => fetchData(buildParams())} disabled={loading} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer disabled:tw-opacity-50">
              <FaSyncAlt size={12} className={loading ? 'tw-animate-spin' : ''} />
              Reload
            </button>
            <button type="button" onClick={() => dispatch(exportEstimateAssignmentSchedule(buildParams()))} disabled={exporting || loading} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-green-300 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-text-sm tw-font-medium tw-cursor-pointer disabled:tw-opacity-50">
              <FaFileExcel size={13} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-48 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Q#, J#, project name..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {search && (
              <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Request Date */}
          <DateRangePicker
            label="Request Date"
            startDate={quickReqDate.startDate}
            endDate={quickReqDate.endDate}
            onChange={handleReqDateChange}
          />

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Schedule Date */}
          <DateRangePicker
            label="Schedule Date"
            startDate={quickSchDate.startDate}
            endDate={quickSchDate.endDate}
            onChange={handleSchDateChange}
          />

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Release Date */}
          <DateRangePicker
            label="Release Date"
            startDate={quickRelDate.startDate}
            endDate={quickRelDate.endDate}
            onChange={handleRelDateChange}
          />

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Sales Unit */}
          <SalesUnitGroupedDropdown
            units={activeSalesUnitsData}
            selectedIds={quickSalesUnitIds}
            onChange={handleSalesUnitChange}
          />

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
              filterTags.length > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
            }`}
          >
            <MdFilterAlt size={15} />
            More Filters
            {filterTags.length > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
                {filterTags.length}
              </span>
            )}
          </button>

          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {totalRows.toLocaleString()} records
            </span>
          )}
        </div>

        {filterTags.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">{Array.isArray(tag.value) ? tag.value.join(' – ') : tag.value}</span>
                <button type="button" onClick={() => removeTag(tag)} className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors">
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <button type="button" onClick={handleResetFilter} className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-xs tw-text-red-500 hover:tw-bg-red-500 hover:tw-text-white tw-border tw-border-red-200 hover:tw-border-red-500 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-font-medium">
              <FaTimes size={8} />
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <AgGridTable
          rowData={rowData}
          columnDefs={COLUMN_DEFS}
          loading={loading}
          pagination={false}
          filter={false}
          height="max(320px, calc(100dvh - 290px))"
          onSortChanged={handleSort}
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalRows}
        perPage={perPage}
        pageSizeOptions={[20, 50, 100, 200]}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
        disabled={loading}
      />

      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
        fields={FILTER_FIELDS}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        title="Filter Estimate Assignment Schedule"
      />
    </div>
  );
};

export default EstimateAssignmentSchedule;
